// UserPathsVisualization.js
import React, { useState, useEffect } from "react";
import { ref, get } from "firebase/database";
import { database } from "../firebase";

const UserPathsVisualization = () => {
    const [loading, setLoading] = useState(true);
    const [pathData, setPathData] = useState({
        topPaths: [],
        longestPaths: [],
    });
    const [activeTab, setActiveTab] = useState("longest"); // "longest" or "popular"
    const [expandedUser, setExpandedUser] = useState(null);

    useEffect(() => {
        const fetchPathData = async () => {
            try {
                setLoading(true);
                
                // Fetch user journey data
                const journeysRef = ref(database, "metrics/userJourneys");
                const journeysSnapshot = await get(journeysRef);
                
                if (!journeysSnapshot.exists()) {
                    setLoading(false);
                    return;
                }
                
                const journeysData = journeysSnapshot.val();
                
                // Process user journey data
                const processedData = processJourneyData(journeysData);
                setPathData(processedData);
                
                setLoading(false);
            } catch (error) {
                console.error("Error fetching user path data:", error);
                setLoading(false);
            }
        };
        
        fetchPathData();
        
        // Refresh data every 5 minutes
        const intervalId = setInterval(fetchPathData, 5 * 60 * 1000);
        
        return () => clearInterval(intervalId);
    }, []);
    
    // Process journey data to extract longest and most popular paths
    const processJourneyData = (journeysData) => {
        const allPaths = [];
        const pathFrequency = {}; // To track path popularity
        
        // Iterate through each user
        Object.entries(journeysData).forEach(([userId, userData]) => {
            if (!userData.sessions) return;
            
            // Process each session for this user
            Object.entries(userData.sessions).forEach(([sessionId, session]) => {
                if (!session.path || Object.keys(session.path).length === 0) return;
                
                // Convert path object to array if needed
                const pathArray = Array.isArray(session.path) 
                    ? session.path 
                    : Object.values(session.path);
                
                if (pathArray.length === 0) return;
                
                // Create a simplified path string for frequency counting
                const pathString = pathArray
                    .map(step => `${step.screen}:${step.action}`)
                    .join("->");
                
                // Count frequency of this path
                pathFrequency[pathString] = (pathFrequency[pathString] || 0) + 1;
                
                // Add to all paths with metadata
                allPaths.push({
                    userId,
                    sessionId,
                    pathLength: pathArray.length,
                    pathString,
                    path: pathArray,
                    startTime: formatTimestamp(session.startedAt),
                    deviceType: session.deviceType || "unknown",
                    isNewUser: session.isNewUser || false
                });
            });
        });
        
        // Sort paths by length (descending) for longest paths
        const longestPaths = [...allPaths].sort((a, b) => b.pathLength - a.pathLength).slice(0, 10);
        
        // Get most popular paths
        const popularPaths = Object.entries(pathFrequency)
            .map(([pathString, count]) => {
                // Find the first path object with this pathString
                const pathObj = allPaths.find(p => p.pathString === pathString);
                return {
                    pathString,
                    count,
                    // Include metadata from the original path object
                    ...pathObj
                };
            })
            .sort((a, b) => b.count - a.count)
            .slice(0, 10);
        
        return {
            topPaths: popularPaths,
            longestPaths: longestPaths
        };
    };
    
    const formatTimestamp = (timestamp) => {
        if (!timestamp) return "N/A";
        
        // Handle different timestamp formats
        let date;
        if (typeof timestamp === 'number') {
            date = new Date(timestamp);
        } else if (timestamp.$date) {
            date = new Date(timestamp.$date);
        } else if (timestamp.seconds) {
            date = new Date(timestamp.seconds * 1000);
        } else {
            try {
                date = new Date(timestamp);
            } catch (e) {
                return "Invalid date";
            }
        }
        
        return date.toLocaleString();
    };
    
    const toggleUserExpand = (userId) => {
        if (expandedUser === userId) {
            setExpandedUser(null);
        } else {
            setExpandedUser(userId);
        }
    };

    const getPathsToDisplay = () => {
        return activeTab === "longest" ? pathData.longestPaths : pathData.topPaths;
    };
    
    // Render a single step in the user journey
    const renderPathStep = (step, index, totalSteps) => {
        return (
            <div className="path-step" key={index}>
                <div className="step-content">
                    <div className="step-header">
                        <span className="step-number">{index + 1}</span>
                        <span className="step-screen">{step.screen}</span>
                    </div>
                    <div className="step-action">{step.action}</div>
                    <div className="step-time">{formatTimestamp(step.timestamp)}</div>
                </div>
                {index < totalSteps - 1 && (
                    <div className="step-connector">
                        <svg width="24" height="24" viewBox="0 0 24 24">
                            <path fill="#9CA3AF" d="M16.59 8.59L12 13.17 7.41 8.59 6 10l6 6 6-6-1.41-1.41z" />
                        </svg>
                    </div>
                )}
            </div>
        );
    };

    if (loading) {
        return <div className="loading-message">Loading user path data...</div>;
    }

    // Display when no data is available
    if (pathData.longestPaths.length === 0 && pathData.topPaths.length === 0) {
        return (
            <div className="user-paths-container">
                <h2>User Journeys</h2>
                <div className="no-data-message">
                    No user journey data available. As users interact with your app, their paths will appear here.
                </div>
            </div>
        );
    }

    return (
        <div className="user-paths-container">
            <h2>User Journeys</h2>
            
            {/* Tab selector */}
            <div className="path-tabs">
                <button 
                    className={`tab-button ${activeTab === "longest" ? "active" : ""}`}
                    onClick={() => setActiveTab("longest")}
                >
                    Longest Journeys
                </button>
                <button 
                    className={`tab-button ${activeTab === "popular" ? "active" : ""}`}
                    onClick={() => setActiveTab("popular")}
                >
                    Most Popular Paths
                </button>
            </div>
            
            {/* Path list */}
            <div className="path-list">
                <table className="paths-table">
                    <thead>
                        <tr>
                            <th>User</th>
                            <th>Steps</th>
                            {activeTab === "popular" && <th>Count</th>}
                            <th>Device</th>
                            <th>Start Time</th>
                            <th>New User</th>
                            <th>Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {getPathsToDisplay().map((pathInfo, index) => (
                            <React.Fragment key={`${pathInfo.userId}-${pathInfo.sessionId}-${index}`}>
                                <tr className="path-row">
                                    <td className="user-cell">{pathInfo.userId.substring(0, 8)}...</td>
                                    <td className="steps-cell">{pathInfo.pathLength}</td>
                                    {activeTab === "popular" && (
                                        <td className="count-cell">{pathInfo.count}</td>
                                    )}
                                    <td className="device-cell">{pathInfo.deviceType}</td>
                                    <td className="time-cell">{pathInfo.startTime}</td>
                                    <td className="new-user-cell">
                                        {pathInfo.isNewUser ? (
                                            <span className="new-user-badge">Yes</span>
                                        ) : "No"}
                                    </td>
                                    <td className="actions-cell">
                                        <button 
                                            onClick={() => toggleUserExpand(pathInfo.userId + pathInfo.sessionId)}
                                            className="view-path-button"
                                        >
                                            {expandedUser === pathInfo.userId + pathInfo.sessionId ? "Hide Path" : "View Path"}
                                        </button>
                                    </td>
                                </tr>
                                {expandedUser === pathInfo.userId + pathInfo.sessionId && (
                                    <tr className="expanded-path-row">
                                        <td colSpan={activeTab === "popular" ? 7 : 6}>
                                            <div className="path-detail">
                                                <h4>User Journey Path</h4>
                                                <div className="path-steps">
                                                    {pathInfo.path.map((step, stepIndex) => 
                                                        renderPathStep(step, stepIndex, pathInfo.path.length)
                                                    )}
                                                </div>
                                            </div>
                                        </td>
                                    </tr>
                                )}
                            </React.Fragment>
                        ))}
                    </tbody>
                </table>
            </div>

            <style jsx>{`
                .user-paths-container {
                    margin-bottom: 30px;
                    background-color: white;
                    border-radius: 8px;
                    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
                    padding: 20px;
                }

                h2 {
                    margin-top: 0;
                    margin-bottom: 16px;
                    font-size: 1.5rem;
                    color: #111827;
                }

                .path-tabs {
                    display: flex;
                    border-bottom: 1px solid #e5e7eb;
                    margin-bottom: 16px;
                }

                .tab-button {
                    padding: 8px 16px;
                    background: none;
                    border: none;
                    border-bottom: 2px solid transparent;
                    cursor: pointer;
                    font-size: 14px;
                    color: #6b7280;
                }

                .tab-button.active {
                    color: #2563eb;
                    border-bottom-color: #2563eb;
                    font-weight: 500;
                }

                .paths-table {
                    width: 100%;
                    border-collapse: collapse;
                    font-size: 14px;
                }

                .paths-table th,
                .paths-table td {
                    padding: 12px;
                    text-align: left;
                    border-bottom: 1px solid #e5e7eb;
                }

                .paths-table th {
                    font-weight: 600;
                    color: #374151;
                    background-color: #f9fafb;
                }

                .user-cell {
                    font-family: monospace;
                }

                .steps-cell {
                    font-weight: 600;
                    color: #2563eb;
                }

                .count-cell {
                    font-weight: 600;
                    color: #059669;
                }

                .new-user-badge {
                    background-color: #dcfce7;
                    color: #059669;
                    padding: 2px 6px;
                    border-radius: 12px;
                    font-size: 12px;
                    font-weight: 500;
                }

                .view-path-button {
                    background-color: #f3f4f6;
                    border: none;
                    border-radius: 4px;
                    padding: 4px 8px;
                    font-size: 12px;
                    cursor: pointer;
                    color: #4b5563;
                }

                .view-path-button:hover {
                    background-color: #e5e7eb;
                }

                .expanded-path-row {
                    background-color: #f9fafb;
                }

                .path-detail {
                    padding: 12px;
                }

                .path-detail h4 {
                    margin-top: 0;
                    margin-bottom: 12px;
                    font-size: 14px;
                    color: #4b5563;
                }

                .path-steps {
                    display: flex;
                    flex-direction: column;
                    gap: 8px;
                }

                .path-step {
                    display: flex;
                    flex-direction: column;
                    align-items: center;
                }

                .step-content {
                    background-color: white;
                    border: 1px solid #e5e7eb;
                    border-radius: 6px;
                    padding: 8px 12px;
                    width: 100%;
                }

                .step-header {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    margin-bottom: 4px;
                }

                .step-number {
                    background-color: #2563eb;
                    color: white;
                    width: 20px;
                    height: 20px;
                    border-radius: 50%;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: 12px;
                    font-weight: 600;
                }

                .step-screen {
                    font-weight: 600;
                    color: #374151;
                }

                .step-action {
                    color: #6b7280;
                    margin-left: 28px;
                    font-size: 13px;
                }

                .step-time {
                    color: #9ca3af;
                    font-size: 12px;
                    margin-top: 4px;
                    margin-left: 28px;
                }

                .step-connector {
                    height: 24px;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                }

                .no-data-message {
                    padding: 20px;
                    text-align: center;
                    color: #6b7280;
                    background-color: #f9fafb;
                    border-radius: 6px;
                    border: 1px dashed #d1d5db;
                }

                .loading-message {
                    padding: 20px;
                    text-align: center;
                    color: #6b7280;
                }
            `}</style>
        </div>
    );
};

export default UserPathsVisualization;